import type * as geojson from 'geojson';
import * as zarr from 'zarrita';

import {
  ApiForecastTimeSeries,
  ForecastTimeSeriesQuery,
  WeatherColumns,
} from 'src/lib/api/series';

import {GriddedSlice} from './aggregate';
import {WeightGrid, computeBasinWeights, geometryRings, ringsBbox} from './basinWeights';
import {DYNAMICAL_DATASETS, DynamicalDatasetDescriptor, GridSampling} from './datasets';
import {DynamicalUnavailableError, rethrowStaleModuleAsUnavailable} from './errors';
import {IndexWindow, Projector, indexWindowForRange, makeProjector} from './grid';
import {DynamicalDataset, openDataset} from './icechunk';
import {NativeForecast, shapeForecast, variablesForQuery} from './shapeResponse';
import {basinAverageSeriesOffThread} from './workerClient';

const SECONDS_IN_AN_HOUR = 3600;

// Weight grids and basin-averaged native series are cached per geometry object:
// UI interactions (aggregation/unit toggles, chart reloads) rebuild query objects
// but reuse the same site geometry, and neither cache entry should survive the
// geometry it was computed from.
const weightCache = new WeakMap<geojson.Geometry, Map<string, WeightGrid>>();
const seriesCache = new WeakMap<geojson.Geometry, Map<string, Promise<NativeForecast>>>();

function requestedWeatherColumns(query: ForecastTimeSeriesQuery): WeatherColumns[] {
  const weatherColumns = Object.values(WeatherColumns);
  if (!query.columns || query.columns.length === 0) {
    return weatherColumns;
  }
  return weatherColumns.filter((column) => query.columns!.includes(column));
}

/**
 * Coordinate axes as the chosen sampling mode sees them: the store's own cell
 * centers for native reads, or the API H4 ingestion grid (subsampled and/or
 * registration-shifted, see datasets.ts h4Sampling) with `stride` mapping each
 * sampled index back to a store index.
 */
function sampledAxes(
  dataset: DynamicalDataset,
  sampling: GridSampling
): {yCoords: Float64Array; xCoords: Float64Array; stride: number} {
  const config = sampling === 'h4' ? dataset.descriptor.h4Sampling : undefined;
  if (!config) {
    return {yCoords: dataset.coords.yCoords, xCoords: dataset.coords.xCoords, stride: 1};
  }
  const stride = config.stride ?? 1;
  const transform = (coords: Float64Array, offset: number) => {
    const length = Math.ceil(coords.length / stride);
    const out = new Float64Array(length);
    for (let i = 0; i < length; i += 1) {
      out[i] = coords[i * stride] + offset;
    }
    return out;
  };
  return {
    yCoords: transform(dataset.coords.yCoords, config.offsetY ?? 0),
    xCoords: transform(dataset.coords.xCoords, config.offsetX ?? 0),
    stride,
  };
}

function spatialWindow(
  dataset: DynamicalDataset,
  geometry: geojson.Geometry,
  projector: Projector | null,
  sampling: GridSampling
): {yWindow: IndexWindow; xWindow: IndexWindow} {
  const {yCoords, xCoords} = sampledAxes(dataset, sampling);
  const rings = geometryRings(geometry, projector);
  const [minX, minY, maxX, maxY] = ringsBbox(rings);
  const yWindow = indexWindowForRange(yCoords, minY, maxY);
  const xWindow = indexWindowForRange(xCoords, minX, maxX);
  if (yWindow.stop <= yWindow.start || xWindow.stop <= xWindow.start) {
    throw new DynamicalUnavailableError(
      `Site geometry is outside ${dataset.descriptor.datasetId} coverage`
    );
  }
  return {yWindow, xWindow};
}

function getWeights(
  dataset: DynamicalDataset,
  geometry: geojson.Geometry,
  yWindow: IndexWindow,
  xWindow: IndexWindow,
  projector: Projector | null,
  sampling: GridSampling
): WeightGrid {
  // Keyed by store URL rather than dataset id: weights derive from the store's
  // coordinate arrays, and a version bump can change the grid.
  const key = `${dataset.storeUrl}|${sampling}|${yWindow.start}:${yWindow.stop}|${xWindow.start}:${xWindow.stop}`;
  let byKey = weightCache.get(geometry);
  if (!byKey) {
    byKey = new Map();
    weightCache.set(geometry, byKey);
  }
  const cached = byKey.get(key);
  if (cached) {
    return cached;
  }
  const {yCoords, xCoords} = sampledAxes(dataset, sampling);
  const weights = computeBasinWeights(
    geometry,
    yCoords.subarray(yWindow.start, yWindow.stop),
    xCoords.subarray(xWindow.start, xWindow.stop),
    dataset.descriptor.gridKind,
    projector,
    sampling
  );
  byKey.set(key, weights);
  return weights;
}

async function readColumnSeries(
  dataset: DynamicalDataset,
  column: WeatherColumns,
  initIndex: number,
  yWindow: IndexWindow,
  xWindow: IndexWindow,
  weights: WeightGrid,
  stride: number
): Promise<Float64Array> {
  const array = dataset.arrays[column];
  if (!array) {
    throw new DynamicalUnavailableError(`No store variable for column ${column}`);
  }
  // Windows index the sampled axes; scale back to store indices for the read.
  const spatialSelection = [
    zarr.slice(yWindow.start * stride, (yWindow.stop - 1) * stride + 1, stride),
    zarr.slice(xWindow.start * stride, (xWindow.stop - 1) * stride + 1, stride),
  ];
  const selection = dataset.descriptor.hasEnsemble
    ? [initIndex, null, null, ...spatialSelection]
    : [initIndex, null, ...spatialSelection];
  const chunk = (await zarr.get(
    array as zarr.Array<zarr.NumberDataType>,
    selection
  )) as unknown as GriddedSlice;
  return basinAverageSeriesOffThread(chunk, weights);
}

export type LeadPlanEntry = {
  /** Lead hour this output step is labeled with. */
  lead: number;
  /** Store lead index for instantaneous variables (temperature). */
  index: number;
  /** Store lead index whose rate covers this step's interval (precipitation). */
  precipIndex: number;
};

/**
 * Maps the store's lead grid onto the lead grid the HF API serves for this
 * source (see precipTiming / maxLeadHours in datasets.ts).
 */
export function planLeads(
  leadTimeHours: number[],
  descriptor: Pick<DynamicalDatasetDescriptor, 'precipTiming' | 'maxLeadHours'>
): LeadPlanEntry[] {
  const entries: LeadPlanEntry[] = [];
  leadTimeHours.forEach((lead, index) => {
    if (descriptor.precipTiming === 'interval-start') {
      // The last store lead only contributes its rate to the previous step.
      if (index === leadTimeHours.length - 1) {
        return;
      }
      entries.push({lead, index, precipIndex: index + 1});
    } else {
      if (lead <= 0) {
        return;
      }
      entries.push({lead, index, precipIndex: index});
    }
  });
  return descriptor.maxLeadHours != null
    ? entries.filter(({lead}) => lead <= descriptor.maxLeadHours!)
    : entries;
}

async function fetchNativeForecast(
  dataset: DynamicalDataset,
  geometry: geojson.Geometry,
  columns: WeatherColumns[],
  initIndex: number,
  projector: Projector | null,
  sampling: GridSampling
): Promise<NativeForecast> {
  const {yWindow, xWindow} = spatialWindow(dataset, geometry, projector, sampling);
  const weights = getWeights(dataset, geometry, yWindow, xWindow, projector, sampling);
  const {stride} = sampledAxes(dataset, sampling);

  const seriesByColumn = await Promise.all(
    columns.map((column) =>
      readColumnSeries(dataset, column, initIndex, yWindow, xWindow, weights, stride)
    )
  );

  const leadPlan = planLeads(dataset.coords.leadTimeHours, dataset.descriptor);

  const values: NativeForecast['values'] = {};
  columns.forEach((column, columnIndex) => {
    const series = seriesByColumn[columnIndex];
    const trimmed = new Float64Array(leadPlan.length);
    leadPlan.forEach(({index, precipIndex}, outIndex) => {
      trimmed[outIndex] =
        column === WeatherColumns.PRECIPITATION
          ? // mm/s rate (averaged since the previous step) → hourly-mean rate mm/h.
            series[precipIndex] * SECONDS_IN_AN_HOUR
          : series[index];
    });
    // An entirely-NaN column means the store lists this init but its chunks
    // haven't been written yet (or the basin has no data coverage); the API must
    // serve the query rather than the user seeing an empty forecast.
    if (!trimmed.some((value) => Number.isFinite(value))) {
      throw new DynamicalUnavailableError(
        `${dataset.descriptor.datasetId} has no ${column} data at init ` +
          new Date(dataset.coords.initTimesMs[initIndex]).toISOString()
      );
    }
    values[column] = trimmed;
  });

  return {
    initTimeMs: dataset.coords.initTimesMs[initIndex],
    leadTimeHours: leadPlan.map(({lead}) => lead),
    values,
  };
}

function getNativeForecastCached(
  dataset: DynamicalDataset,
  geometry: geojson.Geometry,
  columns: WeatherColumns[],
  initIndex: number,
  projector: Projector | null,
  sampling: GridSampling
): Promise<NativeForecast> {
  // openedAt in the key expires cached series alongside the dataset handle: a
  // recent init's chunks fill in as dynamical ingests more lead times.
  const generation = `${dataset.descriptor.datasetId}|${dataset.storeUrl}|${dataset.openedAt}|`;
  const key = `${generation}${sampling}|${dataset.coords.initTimesMs[initIndex]}|${columns.join(',')}`;
  let byKey = seriesCache.get(geometry);
  if (!byKey) {
    byKey = new Map();
    seriesCache.set(geometry, byKey);
  }
  const cached = byKey.get(key);
  if (cached) {
    return cached;
  }
  // The geometry object can live for the whole session, so entries from expired
  // dataset generations (older openedAt, or a superseded store version) must be
  // dropped here or the map grows for as long as the tab stays open.
  for (const existingKey of byKey.keys()) {
    if (
      existingKey.startsWith(`${dataset.descriptor.datasetId}|`) &&
      !existingKey.startsWith(generation)
    ) {
      byKey.delete(existingKey);
    }
  }
  const promise = fetchNativeForecast(dataset, geometry, columns, initIndex, projector, sampling);
  byKey.set(key, promise);
  promise.catch(() => byKey!.delete(key));
  return promise;
}

export async function fetchDynamicalForecastTimeSeries(
  query: ForecastTimeSeriesQuery,
  geometry: geojson.Geometry,
  localTimeZone: string,
  sampling: GridSampling = 'native'
): Promise<ApiForecastTimeSeries> {
  // The single entry to the direct path, so this boundary covers every lazy
  // zarr/icechunk import a read can trigger.
  try {
    return await fetchForecastTimeSeries(query, geometry, localTimeZone, sampling);
  } catch (error) {
    rethrowStaleModuleAsUnavailable(error);
  }
}

async function fetchForecastTimeSeries(
  query: ForecastTimeSeriesQuery,
  geometry: geojson.Geometry,
  localTimeZone: string,
  sampling: GridSampling
): Promise<ApiForecastTimeSeries> {
  const descriptor: DynamicalDatasetDescriptor | undefined = DYNAMICAL_DATASETS[query.source];
  if (!descriptor) {
    throw new DynamicalUnavailableError(`Source ${query.source} is not hosted on dynamical.org`);
  }
  const columns = requestedWeatherColumns(query);
  if (columns.length === 0) {
    throw new DynamicalUnavailableError('No weather columns requested');
  }
  const initTimes = query.initializationTimes ?? [];
  if (initTimes.length === 0) {
    throw new DynamicalUnavailableError('No initialization times requested');
  }

  const dataset = await openDataset(descriptor);

  // Every requested init must exist exactly in the store; otherwise the API path
  // must serve the query so users never silently see fewer forecasts.
  const initIndices = initTimes.map((initTime) => {
    const index = dataset.coords.initTimesMs.indexOf(initTime.getTime());
    if (index < 0) {
      throw new DynamicalUnavailableError(
        `Init time ${initTime.toISOString()} not in ${descriptor.datasetId}`
      );
    }
    return index;
  });

  const projector =
    descriptor.gridKind === 'projected' && dataset.coords.spatialRefAttrs
      ? makeProjector(dataset.coords.spatialRefAttrs)
      : null;

  const zone = query.timeZone ?? localTimeZone;
  const natives = await Promise.all(
    initIndices.map((initIndex) =>
      getNativeForecastCached(dataset, geometry, columns, initIndex, projector, sampling)
    )
  );

  const forecasts = natives
    .map((native) => shapeForecast(native, query, zone))
    .sort((a, b) => a.initializationTime.getTime() - b.initializationTime.getTime());

  return {
    query,
    forecasts,
    variables: variablesForQuery(query, columns),
  };
}
